import {Injectable} from '@angular/core';
import {FormArray, FormBuilder, FormControl, Validators, AbstractControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {OrderDetail} from '@modules/job/models/job.model';
import {tap} from "rxjs";

export type JobFeeItemFormType = {
  name: FormControl<string>;
  rate: FormControl<number>;
  quantity: FormControl<number>;
  taxEnabled: FormControl<boolean>;
  officialFee: FormControl<number>;
  isContactToClient: FormControl<boolean>;
  billingCycleLabel?: FormControl<string | null>;
  orderDetail: FormControl<OrderDetail | null>;
};

export type JobFeeFormType = {
  items: FormArray<JobFeeItemForm>;
  subtotal: FormControl<number>;
  tax: FormControl<number>;
  officialFees: FormControl<number>;
  total: FormControl<number>;
};

export const integerValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (control.value === null || control.value === undefined || control.value === '') {
    return null;
  }
  const value = Number(control.value);
  if (Number.isInteger(value)) {
    return null;
  }
  return { integer: true };
};

export class JobFeeItemForm extends FormGroup<JobFeeItemFormType> {
  constructor() {
    super(
      {
        name: new FormControl<string>('', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        rate: new FormControl<number>(0, {
          nonNullable: true,
          validators: [Validators.required, Validators.min(0)],
        }),
        quantity: new FormControl<number>(0, {
          nonNullable: true,
          validators: [Validators.required, Validators.min(0), integerValidator],
        }),
        taxEnabled: new FormControl<boolean>(
          { value: false, disabled: true },
          { nonNullable: true },
        ), // Default disabled
        officialFee: new FormControl<number>(
          { value: 0, disabled: true },
          { nonNullable: true },
        ),
        isContactToClient: new FormControl<boolean>(false, { nonNullable: true }),
        billingCycleLabel: new FormControl<string | null>(null),
        orderDetail: new FormControl<OrderDetail | null>(null, { nonNullable: true }),
      },
      {
        validators: [atLeastOneFeePositiveValidator],
      },
    );
  }

  onRateChanges$() {
    return this.controls.rate.valueChanges.pipe(
      tap(() => {
        // TaxEnabled is now always disabled, so no item-specific logic needed here.
        // If there were other effects of rate changes, they would go here.
      }),
    );
  }
}

// Custom validator to ensure at least one fee (rate) is positive
export const atLeastOneFeePositiveValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const group = control as FormGroup<JobFeeItemFormType>;
  const rate = group.controls.rate.value;

  if (rate > 0) {
    return null;
  }

  return { atLeastOneFeePositive: true };
};

@Injectable()
export class JobFeeForm extends FormGroup<JobFeeFormType> {
  currency = 'SGD';
  taxRate = 0;
  hasTax = false;

  constructor(private fb: FormBuilder) {
    super({
      items: new FormArray<JobFeeItemForm>([], Validators.required), // Initialize with new FormArray to resolve super() and type issues
      subtotal: new FormControl<number>(0, {nonNullable: true}),
      tax: new FormControl<number>(0, {nonNullable: true}),
      officialFees: new FormControl<number>(0, {nonNullable: true}),
      total: new FormControl<number>(0, {nonNullable: true}),
    });
  }

  get items(): FormArray<JobFeeItemForm> {
    return this.controls.items;
  }

  // Getters for easier template access (optional, but keeps template cleaner)
  get subtotal(): number { return this.controls.subtotal.value; }
  get tax(): number { return this.controls.tax.value; }
  get officialFees(): number { return this.controls.officialFees.value; }
  get total(): number { return this.controls.total.value; }

  setJobDetails(currency: string, taxRate: number, hasTax: boolean): void {
    this.currency = currency;
    this.taxRate = taxRate;
    this.hasTax = hasTax;
  }

  createByOrderDetail(orderDetail: OrderDetail) {
    const itemForm = new JobFeeItemForm();

    const isContactToClient = orderDetail.service?.paymentType?.isContactToClient || false;
    const serviceFee = orderDetail.service?.serviceFee || 0;
    const officialFee = orderDetail.service?.officialFee || 0;
    const displayName = orderDetail.service?.displayLabel || orderDetail.service?.service?.label || '';
    const billingCycleLabel = orderDetail.service?.paymentType?.cycleLabel || null;

    itemForm.patchValue({
      name: displayName,
      rate: serviceFee,
      quantity: orderDetail.quantity || 1,
      taxEnabled: this.hasTax && this.taxRate > 0, // Set value based on global tax settings
      officialFee,
      isContactToClient,
      billingCycleLabel,
      orderDetail,
    });

    this.items.push(itemForm);

    return itemForm
  }

  calculateTotals(): void {
    let tempSubtotal = 0;
    let tempTax = 0;
    let tempOfficialFees = 0;

    const rawValue = this.items.getRawValue();

    rawValue.forEach((item) => {
      const safeQuantity = Math.max(0, item.quantity || 0);
      const lineServiceFee = (item.rate || 0) * safeQuantity;
      tempSubtotal += lineServiceFee;

      tempOfficialFees += (item.officialFee || 0) * safeQuantity;
    });

    if (this.hasTax) {
      tempTax = tempSubtotal * (this.taxRate / 100);
    }

    this.controls.subtotal.setValue(tempSubtotal);
    this.controls.tax.setValue(tempTax);
    this.controls.officialFees.setValue(tempOfficialFees);

    const calculatedTotal = tempSubtotal + tempTax + tempOfficialFees;
    this.controls.total.setValue(calculatedTotal);
  }

  getLineAmount(index: number): number {
    const item = this.items.at(index).value;
    const safeQuantity = Math.max(0, item.quantity || 0);
    return (item.rate || 0) * safeQuantity;
  }
}
